import { useState, useContext } from 'react'
import { useEffectOnce } from 'usehooks-ts'
import styled from 'styled-components'
import GridItem from 'components/GridItem'
import dataDE from 'data.json'
import dataFR from 'data-fr.json'
import { nest } from 'd3-collection'
import Legend from 'components/Legend'
import GlobalContext from 'Contexts/GlobalContext'
import { de } from 'Globals/locale'

interface colorObject {
  farleft: string
  left: string
  right: string
  farright: string
  middlepopulist: string
  middle: string
}

const Grid = ({
  tenant,
  colorMode,
  titleText,
  subTitleText,
  colors,
}: {
  tenant?: object
  colorMode?: string
  titleText?: string
  subTitleText?: string
  colors: colorObject
}) => {
  const [isMobile, setIsMobile] = useState(false)
  const [activeTooltip, setActiveTooltip] = useState(-1)
  const { locale } = useContext(GlobalContext) ?? { locale: de }
  const data = (tenant as { lang: string }).lang == 'fr' ? dataFR : dataDE

  const handleResize = (): void => {
    setIsMobile(window.innerWidth < 600)
  }

  useEffectOnce(() => {
    // set height when window is resized
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  })

  const countryData = nest()
    .key(function (d: any) {
      return d['Land_DE']
    })
    .entries(data)
  // console.log('data:', data)
  // console.log(countryData)

  return (
    <>
      <GraphicWrapper className={isMobile ? 'mobile' : 'desktop'}>
        {titleText && <h3>{titleText}</h3>}
        {subTitleText && <div className='sub'>{subTitleText}</div>}
        <Legend
          terms={[
            locale.farright,
            locale.rightpopulist,
            locale.populists,
            locale.leftpopulist,
            locale.farleft,
            locale.others,
          ]}
          colors={[
            colors.farright,
            colors.right,
            colors.middlepopulist,
            colors.left,
            colors.farleft,
            colors.middle,
          ]}
        />
        <GridWrapper className={isMobile ? 'mobile' : 'desktop'}>
          {countryData.map(function (item: any, itemIndex: number) {
            // console.log('item:', item)
            const yearData = nest()
              .key(function (d: any) {
                return d['Jahr']
              })
              .entries(item.values)
            // console.log('yearData:', yearData)
            return (
              <GridItem
                name={item.key}
                index={itemIndex}
                colorMode={colorMode}
                key={itemIndex}
                data={yearData}
                colors={colors}
                iso={item.values[0]['ISO'].toLowerCase()}
                activeTooltip={activeTooltip}
                setActiveTooltip={setActiveTooltip}
              ></GridItem>
            )
          }, [])}
        </GridWrapper>
        <Source>
          <div>
            {locale.sources}: <a href='https://popu-list.org'>The Populist</a>,{' '}
            <a href='https://www.parlgov.org'>ParlGov</a>, {locale.research}
          </div>
        </Source>
      </GraphicWrapper>
    </>
  )
}
const GraphicWrapper = styled.div`
  max-width: 1111px;
  margin: 0 auto;
  padding: 2rem;

  h3 {
    margin-bottom: 0.4rem;
  }
  .sub {
    font-size: 1.6rem;
    line-height: 2.3rem;
    margin-bottom: 1.6rem;
  } 
`

const GridWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 1rem;
  @media only screen and (min-width: 600px) {
    grid-template-columns: repeat(3, 1fr);
    grid-gap: 3.4rem;
  }
  @media only screen and (min-width: 769px) {
    grid-template-columns: repeat(4, 1fr);
  }
  @media only screen and (min-width: 1024px) {
    grid-template-columns: repeat(5, 1fr);
  }
`

const Source = styled.div`
  _padding-left: 1rem;
  @media only screen and (min-width: 600px) {
    padding-top: 1rem;
  }
  div,
  a {
    color: var(--gray-3);
    font-size: 1.2rem;
    line-height: 2rem;
    margin-top: 0.3rem;
    margin-bottom: 0;
  }
  a {
    color: var(--blue-main);
    text-decoration: none;
  }
`

export default Grid
